import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from '../context/ThemeContext';
import { AuthProvider } from '../context/AuthContext';
import { TenantProvider } from '../context/TenantContext';
import { ToastProvider } from '../components/ui';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ScrollToTop } from '../components/ScrollToTop';
import { AppRoutes } from './routes';
import { useSessionRefresh } from '../hooks/useSessionRefresh';
import { useGoogleOneTap } from '../hooks/useGoogleOneTap';
import '../styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent: React.FC = () => {
  useSessionRefresh();
  useGoogleOneTap({ enabled: false });
  return <AppRoutes />;
};

export const App: React.FC = () => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorBoundary>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h1>Application Error</h1>
            <p>Something went wrong. Our team has been notified.</p>
            <details style={{ marginTop: '1rem', textAlign: 'left' }}>
              <summary>Error Details</summary>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {error instanceof Error ? error.toString() : String(error)}
              </pre>
            </details>
            <button
              onClick={resetError}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          </div>
        </ErrorBoundary>
      )}
      showDialog
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ToastProvider>
            <BrowserRouter>
              <ScrollToTop />
              <AuthProvider>
                <TenantProvider>
                  <AppContent />
                </TenantProvider>
              </AuthProvider>
            </BrowserRouter>
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};
